import { motion } from "framer-motion";
import { Layers, Image, Workflow, Sparkles, ShoppingCart, Users, BarChart3, Search, FileSearch, Plug, CloudUpload } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const CAPS = [
  { label: "Adobe Experience Manager", Icon: Layers },
  { label: "Digital Asset Management", Icon: Image },
  { label: "Content Supply Chain", Icon: Workflow },
  { label: "Personalization", Icon: Sparkles },
  { label: "Commerce", Icon: ShoppingCart },
  { label: "Customer Data Platforms", Icon: Users },
  { label: "Analytics", Icon: BarChart3 },
  { label: "Search", Icon: Search },
  { label: "SEO Automation", Icon: FileSearch },
  { label: "API Integrations", Icon: Plug },
  { label: "Cloud Modernization", Icon: CloudUpload },
];

const NODES = [
  { label: "Experience Layer", items: "AEM · Web · Commerce", x: "50%", y: "6%" },
  { label: "Intelligence Layer", items: "Personalization · Search · SEO", x: "8%", y: "42%" },
  { label: "Data Layer", items: "CDP · Analytics · DAM", x: "50%", y: "78%" },
  { label: "Integration Layer", items: "APIs · Cloud · Supply Chain", x: "92%", y: "42%" },
];

export const EnterpriseExperience = () => (
  <section data-testid="enterprise-experience-section" className="py-24 sm:py-32 bg-jx-night text-white relative overflow-hidden">
    <div className="pointer-events-none absolute -bottom-40 left-1/4 w-[600px] h-[400px] rounded-full bg-jx-copper/15 blur-[140px]" />
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <FadeUp>
            <div data-testid="experience-ecosystem-visual" className="relative rounded-3xl jx-glass-dark p-6 sm:p-8 aspect-[4/3.4]">
              <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="ecoGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D12D8C" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#E86A33" stopOpacity="0.6" />
                  </linearGradient>
                </defs>
                <motion.path d="M 50 18 L 16 46 L 50 82 L 84 46 Z" fill="none" stroke="url(#ecoGrad)" strokeWidth="0.4" strokeDasharray="2 2"
                  initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2, ease: "easeInOut" }} />
                <motion.line x1="50" y1="18" x2="50" y2="82" stroke="url(#ecoGrad)" strokeWidth="0.3" strokeDasharray="1.5 2"
                  initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, delay: 0.6 }} />
                <motion.line x1="16" y1="46" x2="84" y2="46" stroke="url(#ecoGrad)" strokeWidth="0.3" strokeDasharray="1.5 2"
                  initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, delay: 0.8 }} />
              </svg>
              <motion.div
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-2xl jx-gradient-bg flex flex-col items-center justify-center shadow-[0_0_60px_rgba(209,45,140,0.35)]"
              >
                <span className="font-display font-extrabold text-white text-[10px] tracking-widest text-center leading-tight px-2">EXPERIENCE<br />CORE</span>
              </motion.div>
              {NODES.map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.18, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute -translate-x-1/2 rounded-xl bg-white/[0.07] border border-white/12 px-4 py-3 text-center backdrop-blur-md"
                  style={{ left: n.x, top: n.y }}
                >
                  <p className="text-[11px] font-bold tracking-widest uppercase text-white">{n.label}</p>
                  <p className="text-[10px] text-white/50 mt-1 whitespace-nowrap">{n.items}</p>
                </motion.div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <SectionHeading
            dark
            testId="enterprise-experience"
            overline="Enterprise Digital Experience"
            title={<>Digital experiences <span className="jx-gradient-text">built for scale.</span></>}
            body="We architect enterprise experience ecosystems where content, commerce, data and intelligence operate as one connected platform."
          />
          <div className="mt-10 flex flex-wrap gap-2.5">
            {CAPS.map((c, i) => (
              <motion.span
                key={c.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                whileHover={{ y: -3 }}
                data-testid={`exp-cap-${i}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] px-4 py-2 text-xs font-medium text-white/80 hover:border-jx-magenta/50 transition-colors duration-300 cursor-default"
              >
                <c.Icon className="w-3.5 h-3.5 text-jx-copper" strokeWidth={1.8} />
                {c.label}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
